import { Injectable } from '@angular/core';
import { HttpMethodType } from '@app/app.constant';
import { HttpCallService } from '@app/core/services/http-call.service';
import { INotificationTaskHttpResponse, IUserNotificationTemplateAlertInfoHttpResponse, NotificationTask, NotificationTemplateSaveModel, UserNotificationTemplateAlertInfo } from '@app/models/notification-settings';
import { BehaviorSubject, Observable, Subject, map, tap } from 'rxjs';

const HttpServiceMethods = {
  NotificationTask: 'NotificationTask',
  GetMyNotificationTaskList: 'NotificationTask/GetMyNotificationTaskList',
  HasMyNotificationTask: 'NotificationTask/HasMyNotificationTask',
  NotificationTaskRead: 'NotificationTask/NotificationTaskRead',
  NotificationTaskReadAll: 'NotificationTask/NotificationTaskReadAll',
  NotificationTaskClear: 'NotificationTask/NotificationTaskClear',
  NotificationTaskClearAll: 'NotificationTask/NotificationTaskClearAll',
  NotificationTaskUnRead: 'NotificationTask/NotificationTaskUnRead',
  GetMyNotificationTemplateAlertInfo: 'NotificationTask/GetUserNotificationTemplateAlertInfo',
  SaveMyNotificationTemplateAlerts: 'NotificationTask/SaveUserNotificationTemplateAlertInfo'
};

const stubNotification: Array<INotificationTaskHttpResponse> = [{
  id: 1,
  isViewed: true,
  taskDateTime: new Date(),
  messageContent: 'Welcome to evide. We are happy to onboard you',
  inititorUserName: "Parth Hirapra",
  inititorLicense: "Foylesoft",
  taskLink: "/test"
}, {
  id: 4,
  isViewed: false,
  taskDateTime: new Date((new Date()).valueOf() - 1000 * 60 * 60 * 24),
  messageContent: 'We received your feedback 2',
  inititorUserName: "Heena Hirapra",
  inititorLicense: "Samarth Org",
  taskLink: "/profile"
}, {
  id: 5,
  isViewed: false,
  taskDateTime: new Date((new Date()).valueOf() - 1000 * 60 * 60 * 24),
  messageContent: 'We received your feedback 3',
  inititorUserName: "Heena Hirapra",
  inititorLicense: "Samarth Org",
  taskLink: "/profile"
}, {
  id: 2,
  isViewed: false,
  taskDateTime: new Date((new Date()).valueOf() - 1000 * 60 * 60 * 24),
  messageContent: 'We received your feedback',
  inititorUserName: "Heena Hirapra",
  inititorLicense: "Samarth Org",
  taskLink: "/profile"
}, {
  id: 1,
  isViewed: true,
  taskDateTime: new Date((new Date()).valueOf() - 1000 * 60 * 60 * 24 * 5),
  messageContent: 'This is expired notification. We will remove this notification after 30 days. Adding more text for long text testing. Hope that is fine',
  inititorUserName: "Yilmaz",
  inititorLicense: "Foylesoft",
  taskLink: "/dashboard"
}]

@Injectable({
  providedIn: 'root'
})
export class NotificationTasksService {
  public notificationTaskSubject = new Subject<null>();

  public notificationUnReadCountSubject = new BehaviorSubject<number>(0);

  public notificationsTasksSubject = new BehaviorSubject<NotificationTask[]>(null);
  public notificationsUnreadTasksSubject = new BehaviorSubject<NotificationTask[]>(null);

  constructor(private httpService: HttpCallService) { }

  getMyNotificationTaskList(unreadOnly = false): Observable<NotificationTask[]> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetMyNotificationTaskList).pipe(
      // map(() => stubNotification),
      map(res => res.map((item: INotificationTaskHttpResponse) => NotificationTask.adapt(item))),
      map(res => res.sort((a, b) => +b.taskDateTime - +a.taskDateTime)),
      map(res => res.sort((a, b) => +a.isViewed - +b.isViewed)),
      tap(res => this.handleNotificationTaskListRes(res)),
      map(res => unreadOnly ? res.filter((item) => !item.isViewed) : res),
    );
  }

  handleNotificationTaskListRes = (res: NotificationTask[]) => {
    this.notificationsTasksSubject.next(res)
    this.notificationUnReadCountSubject.next(res.filter((not) => !not.isViewed).length);
    this.notificationsUnreadTasksSubject.next(res.filter((not) => !not.isViewed));
  }

  hasMyNotificationTask(): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.HasMyNotificationTask);
  }

  notificationTaskReadAll(): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, `${HttpServiceMethods.NotificationTaskReadAll}`)
      .pipe(tap(() => { this.notificationTaskSubject.next(null); }));
  }

  notificationTaskClearAll(): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, `${HttpServiceMethods.NotificationTaskClearAll}`)
      .pipe(tap(() => { this.notificationTaskSubject.next(null); }));
  }

  notificationTaskUnRead(notificationTaskId: number): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, `${HttpServiceMethods.NotificationTaskUnRead}?notificationTaskId=${notificationTaskId}`)
      .pipe(tap(() => { this.notificationTaskSubject.next(null); }));
  }

  notificationTaskClear(notificationTaskId: number): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, `${HttpServiceMethods.NotificationTaskClear}?notificationTaskId=${notificationTaskId}`)
      .pipe(tap(() => { this.notificationTaskSubject.next(null); }));
  }

  notificationTaskRead(notificationTaskId: number): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, `${HttpServiceMethods.NotificationTaskRead}?notificationTaskId=${notificationTaskId}`)
      .pipe(tap(() => { this.notificationTaskSubject.next(null); }));
  }

  getMyNotificationTemplateAlertInfos(): Observable<UserNotificationTemplateAlertInfo[]> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetMyNotificationTemplateAlertInfo).pipe(
      map(res => res.map((item: IUserNotificationTemplateAlertInfoHttpResponse) => UserNotificationTemplateAlertInfo.adapt(item)))
    );
  }

  saveMyNotificationTemplateAlerts(notificationTemplate: NotificationTemplateSaveModel) {
    return this.httpService.callService(HttpMethodType.POST, `${HttpServiceMethods.SaveMyNotificationTemplateAlerts}`, notificationTemplate);
  }
}
